// DRAFT copy (TECH-867): built as the ticket writes it; Stuart approves before POLLS_ENABLED goes on.
// Every string the live poll surfaces render. On screen it is "answer", never the other word. No "{n} here"
// count anywhere (TECH-867 § Decided "One count").

const answersCount = (n: number): string => (n === 1 ? '1 answer' : `${n} answers`);

export const LIVE_POLL_COPY = {
  // The big screen (TECH-867 § Views B).
  /** Stored as written; the screen uppercases it with CSS (RESOLUTIONS L10). */
  eyebrowQuestions: (n: number): string =>
    n === 1 ? 'Live poll · 1 question' : `Live poll · ${n} questions`,
  scanToParticipate: 'Scan to participate',
  startingSoon: 'Starting soon',
  getReady: 'Get ready',
  questionOneComingUp: 'Question 1 is coming up',
  questionOfCount: (n: number, m: number, k: number): string =>
    `Question ${n} of ${m} · ${answersCount(k)}`,
  share: (share: number): string => `${share}%`,
  fullScreen: 'Full screen',
  resultsEyebrow: (n: number): string =>
    n === 1 ? 'Results · 1 question' : `Results · ${n} questions`,
  resultsHeading: "Here's what the room said",
  numberedQuestion: (n: number, question: string): string => `${n}. ${question}`,
  answers: answersCount,
  /** The results' small line when more answers tie than get a bar; n counts every tied one. A new DRAFT string. */
  answersAndTied: (k: number, n: number): string => `${answersCount(k)} · ${n} tied`,
  /** The line beside the logo on the end panel; the headline under it is the Space's name alone. */
  joinLabel: 'Join',
  peopleMayBeHere: "The people you're looking for may already be here.",
  scanToJoin: 'Scan to join, or go to',
  pageOf: (p: number, q: number, s: number): string => `Page ${p} of ${q} · next page in ${s} s`,
  // AIDEV-NOTE: the ticket's paused line (§ Views B, B3c); a display-only screen shows it too, so a paused
  // page never claims to rotate.
  pagePaused: (p: number, q: number): string => `Page ${p} of ${q} · paused`,
  removedTitle: 'This live poll has ended',
  removedLine: (space: string | null): string =>
    space ? `Thanks for answering. ${space} keeps going on meetball.` : 'Thanks for answering.',
  reconnecting: 'Reconnecting…',
  wordmark: 'meetball',

  // The author's own screen (B2b, B3c; TECH-867 § Views B). Only the signed-in author sees these.
  back: 'Back',
  nextQuestion: 'Next question',
  showResults: 'Show results',
  endPoll: 'End poll',
  pausePages: 'Pause pages',
  resumePages: 'Resume pages',
  nextPage: 'Next page',

  // The one End confirm (L13 = H6), and F4's before any question was shown.
  endConfirmTitle: 'End this live poll?',
  endConfirmBody: 'Everyone sees the results.',
  endWithoutQuestionsTitle: 'End live poll without asking any questions?',
  cancel: 'Cancel',
  // New (FE-L2b review, BE-L3-fix2's 409 LIVE_POLL_ARCHIVED), no frame.
  liveArchived: "This live poll is archived and can't be reopened.",

  // E3: a wrong, unknown or expired live link.
  livePoll: 'Live poll',
  linkDoesntWorkTitle: "This link doesn't work",
  linkDoesntWorkLine: 'Check the link, or ask the host to show the QR code again.',

  // The live card in the Post sheet and the feed (FE-L3a; Polls page L6–L8, live D1–D4, E1, E2, D3b).
  live: 'Live',
  cardStartingSoon: (n: number): string =>
    n === 1
      ? 'Live poll · 1 question · starting soon'
      : `Live poll · ${n} questions · starting soon`,
  hangTight: 'Hang tight, it starts soon',
  keepThisOpen: 'Keep this open. The first question appears here when the host starts.',
  questionOneComingUpLine: 'Question 1 is coming up. It appears here when the host shows it.',
  questionPickOne: (n: number, m: number): string => `Question ${n} of ${m} · Pick one`,
  questionPickMany: (n: number, m: number): string => `Question ${n} of ${m} · Pick one or more`,
  answerToSeeResults: 'Answer to see results',
  answerIsIn: 'Your answer is in. You can change it until the host moves on.',
  finalResults: (n: number): string =>
    n === 1
      ? 'Live poll · Final results · 1 question'
      : `Live poll · Final results · ${n} questions`,
  /** "Ended Sat 8 Nov". */
  endedOn: (date: string): string => `Ended ${date}`,
  peopleAnswered: (n: number): string => (n === 1 ? '1 person answered' : `${n} people answered`),
  /** An ended live poll's card in a feed, past its first 2 questions (founder, 3 Nov). */
  seeAllQuestions: (n: number): string => `See all ${n} questions`,
  noAccountNeeded: "You don't need an account to answer.",
  /** The join card's small line before question 1; `noAccountNeeded` stays the line under a question. */
  noAccountToParticipate: "You don't need an account to participate in this poll.",
  questionClosed: 'That question closed. This is the one open now.',
  didntSend: "Didn't send. Tap to try again",
  answerInJoined: (space: string): string => `Your answer is in. You've joined ${space}.`,
  shareLabel: 'Share',

  // The join card (FE-L3b; live D1, D1b, D1d–D1f, D5–D8; § The end call to action; RESOLUTIONS F5, F8, F9).
  spaceOnMeetball: (space: string): string => `${space} on meetball`,
  thanksForAnswering: 'Thanks for answering',
  whoElseIsHere: 'While you wait… who else is in this room?',
  joinCardLine: (space: string): string =>
    `Join ${space} on meetball to see what people here are looking for, and share what you need.`,
  yourEmail: 'Your email',
  joinSpaceButton: (space: string): string => `Join ${space}`,
  askToJoinSpace: (space: string): string => `Ask to join ${space}`,
  needThePin: "You'll need the PIN from the host.",
  hostsApprove: 'The hosts approve new members.',
  joinInOneTap: 'Join in one tap.',
  answersComeWithYou: 'Your answers come with you.',
  // D1b, the sign-in step in place. AIDEV-NOTE: the frame says "Check your email for a code" / "We sent it to …",
  // but today's sign-in emails a LINK, not a code, and the app has no code field to type one into: the title drops
  // "for a code" and the line names the link (reported as a gap). "Wrong email? Change it" as the frame.
  checkYourEmail: 'Check your email',
  sentTo: (email: string): string =>
    `We sent a sign-in link to ${email}. Open it, and you'll come back to this poll.`,
  /** At the end there is no first question to wait for (founder, 3 Nov). */
  sentToAtEnd: (email: string): string =>
    `We sent a link to ${email}. You can stay here: the results aren't going anywhere.`,
  wrongEmail: 'Wrong email? Change it',
  /** No ticket copy (TECH-867 § To check: "after D5's join a toast 'You've joined {Space}.'"). */
  joinedSpace: (space: string): string => `You've joined ${space}.`,

  // Errors and the room's other moments (FE-L3c; TECH-867 § Errors, E5, E6, E8, E9; L14, L14b; D1's link row).
  /** E5: "12 Oct" (the viewer's clock and zone). */
  endedOnDate: (date: string): string => `This live poll ended on ${date}`,
  resultsStayHere: (space: string): string =>
    `The results stay here. ${space} is still going: have a look around.`,
  cantAnswerTitle: "You can't answer this poll",
  goToMeetball: 'Go to meetball',
  /** E8 (F12: the ticket's words; the frame drops the reason). The title is `removedTitle`. */
  takenDown: (space: string): string =>
    `It was taken down. ${space} is still here: have a look around.`,
  startedALivePoll: (author: string): string => `${author} started a live poll`,
  rejoin: 'Rejoin',
  /** The LIVE bar before Start; the big screen's own "Starting soon" is `startingSoon`. */
  barStartingSoon: 'Live poll starting soon',
  whileYouWait: (space: string): string =>
    `While you wait, see what people in ${space} are looking for`,
} as const;

// The composer in live mode, saving, the not-started card and the ⋯ menu (FE-L1a; TECH-867 L2–L5,
// § ⋯ menu on a live poll). DRAFT, as above.
const optionsCount = (n: number): string => (n === 1 ? '1 option' : `${n} options`);

export const LIVE_POLL_COMPOSER_COPY = {
  // L2, L3: question 1 folded, the open question, Add question (RESOLUTIONS F2: {n} = questions in it).
  foldedNumber: (n: number, question: string): string => `${n}. ${question} `,
  foldedOptions: (k: number): string => `· ${optionsCount(k)}`,
  /** The whole folded row, as one accessible name: "1. {question} · {n} options". */
  foldedRow: (n: number, question: string, k: number): string =>
    `${n}. ${question} · ${optionsCount(k)}`,
  questionN: (n: number): string => `Question ${n}`,
  // AIDEV-NOTE: the frame's trash has no words; this screen-reader name is a new DRAFT string.
  removeQuestionN: (n: number): string => `Remove question ${n}`,
  addQuestion: 'Add question',
  addQuestionCount: (n: number): string => `${n} of 10`,
  addQuestionLabel: (n: number): string => `Add question ${n} of 10`,
  qrHint: 'People can scan the QR code and answer straight away, even without a meetball account.',
  saveLivePoll: 'Save live poll',

  // L2b: turning the switch off with two or more questions.
  turnOffTitle: 'Turn off live poll?',
  turnOffBody: (questions: number): string =>
    questions <= 2
      ? 'Question 2 will be removed. Question 1 stays as your poll.'
      : `Questions 2 to ${questions} will be removed. Question 1 stays as your poll.`,
  cancel: 'Cancel',
  turnOff: 'Turn off',

  // L4: the place chooser in live mode (RESOLUTIONS L3, L4).
  pickerTitle: 'Where will you run it?',
  pickerSubtitle: "One Space or one Event where you're a host.",
  pickerSearch: 'Search Spaces and Events…',
  pickerFooter: 'A live poll runs in one place',
  pickerDone: 'Done',
  eventRowMeta: (when: string): string => `Event · ${when}`,

  // L5: the card before Start, the author's only.
  notStarted: 'Not started',
  livePollQuestions: (n: number): string =>
    n === 1 ? 'Live poll · 1 question' : `Live poll · ${n} questions`,
  onlyYouUntilStart: 'Only you can see this until you start it.',
  runLivePoll: 'Run live poll',
  /** The byline's age ("2d"); "now" reads "saved now" (a new DRAFT form, the byline's own word). */
  savedAgo: (age: string): string =>
    age === 'now' || age === '' ? 'saved now' : `saved ${age} ago`,

  // The ⋯ menu on a live poll (TECH-867 § ⋯ menu); Bookmark, Report, Delete as on any poll.
  menuEdit: 'Edit',
  menuEndPollNow: 'End poll now',
  menuPinPoll: 'Pin poll',
} as const;

// The host view (H1–H7), the host controls (L10–L12), Your live polls (H5, H5b) and the author's live card (L9,
// L9b): FE-L1b, TECH-867 § Views H, L. DRAFT, as above.
const questionsCount = (n: number): string => (n === 1 ? '1 question' : `${n} questions`);

export const LIVE_HOST_COPY = {
  /** The app's back arrow (no words drawn); "Back" is the host controls' own button. */
  back: 'Go back',
  hostViewOnlyYou: 'Host view · only you see this',
  loading: 'Loading the live poll',
  // H1, H1b.
  notStartedEyebrow: 'Live poll · not started',
  startedEyebrow: 'Live poll · started · no question shown yet',
  readyWhenYouAre: 'Ready when you are',
  theBigScreen: 'The big screen',
  bigScreenShowsGetReady: 'The big screen shows “Get ready”',
  copyScreenLink: 'Copy screen link',
  shareScreenLink: 'Share screen link',
  // DRAFT: Customise screen (ping Product for copy).
  customizeScreen: 'Customise screen',
  background: 'Background',
  backgroundColour: 'Background colour',
  pictureScreenOneOnly: 'Pictures only go on Screen 1.',
  yourPicture: 'Your picture',
  addYourOwnPicture: 'Add your own picture',
  whereQrSits: 'Where the QR sits',
  qrBottomRight: 'Bottom right',
  qrBottomLeft: 'Bottom left',
  qrTopRight: 'Top right',
  qrTopLeft: 'Top left',
  dragQrHint: 'Or drag the QR on the preview.',
  screen: 'Screen',
  screenOne: 'Screen 1: Starting soon',
  screenTwo: 'Screen 2: After Start',
  uploadingPicture: 'Uploading…',
  pictureFailedTitle: "Couldn't upload that picture",
  pictureFailedBody: 'Check your connection, or try a different file.',
  picturePixelated:
    'This picture may look pixelated on a big screen. Check it before you start, or upload a higher resolution one.',
  /** H1's helper: "{openOnLaptop}{showItFromThisPhone}{openOnLaptopEnd}", the middle a button. */
  openOnLaptop:
    'Open it on the laptop connected to the projector. Signed in there as you, it has the same controls as this phone. Or ',
  showItFromThisPhone: 'show it from this phone',
  openOnLaptopEnd: '.',
  start: 'Start',
  edit: 'Edit',
  inTheFeedNow:
    'The poll is in the feed now. People who scan see “Get ready” until you show question 1.',
  showQuestionOne: 'Show question 1',
  endWithoutRunning: 'End live poll without asking any questions',
  // H2–H4, L10–L12.
  questionOf: (n: number, m: number): string => `Question ${n} of ${m}`,
  questionOfLast: (n: number, m: number): string => `Question ${n} of ${m} · last question`,
  shareAndCount: (share: number, count: number): string => `${share}% · ${count}`,
  endedEyebrow: 'Ended · the screen shows these results and the join QR',
  results: 'Results',
  pageOf: (p: number, q: number): string => `Page ${p} of ${q}`,
  backToQuestion: (n: number): string => `Back to question ${n}`,
  joinsSummary: "You'll get a summary of who joined 30 minutes after the end.",
  onlyYouSeeControls: 'Only you see these controls. Everyone else sees the open question.',
  backReopens: (n: number): string =>
    `Back reopens question ${n} for answers, everywhere. The other results stay as they are.`,
  // AIDEV-NOTE: L12's own words (the frame's, not in the task's list): its header, title and top-answer rows.
  resultsOnTheScreen: 'Results on the screen',
  topAnswers: 'Top answers',
  topAnswerShare: (answer: string, share: number): string => `${answer} · ${share}%`,
  // H5, H5b: one chip style (RESOLUTIONS L5); "Live" is LiveBadge.
  yourLivePolls: 'Your live polls',
  rowMeta: (place: string | null, n: number, date: string): string =>
    [place, questionsCount(n), date].filter(part => part !== null && part !== '').join(' · '),
  today: 'today',
  chipNotStarted: 'Not started',
  chipEnded: 'Ended',
  noLivePolls: 'No live polls yet',
  noLivePollsBody:
    'To make one, tap the pencil button in your Space, or + on your Event, then choose Poll and turn on Live poll at the top. It waits here until you start it.',
  // L9, L9b.
  openScreen: 'Open screen',
  hostControls: 'Host controls',
  onlyYouSeeHostControls: 'Only you see Host controls.',
} as const;
